<template>
  <div class="tasks-view view">
    <div class="container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #right>
          <Actions placement="complaints" :use-portal="false">
            <template #bottom>
              <ButtonComponent
                :label="$tc('import')"
                tag="router-link"
                :to="{ name: 'import', params: { type: 'task' } }"
                :icon="['fal', 'plus']"
              />
            </template>
          </Actions>

          <ButtonComponent
            @click="trigger"
            :label="$t('createResource', { resource: $tc($path('meta.type', $route), 1).toLowerCase() })"
            :icon="['fal', 'plus']"
            theme="secondary"
          />
        </template>
      </HeaderComponent>
      <FilterComponent
        :persist="false"
        v-model="filter"
        :default="defaultFilter"
        :items="filterItems"
        :options="filterOptions"
      >
        <template #status="{ items }">
          <div class="row g-1">
            <Badge v-if="!items.length || items.length === statuses.length" type="outlined" :value="$t('showAll')" />
            <template v-else>
              <Badge
                v-for="item in items"
                :key="item"
                :theme="statusThemes[item]"
                type="outlined"
                :value="$t(`taskStatus${item.toTitleCase()}`)"
              />
            </template>
          </div>
        </template>
      </FilterComponent>
      <DropdownComponent class="task-list__add" :close-on-click="false">
        <ButtonComponent :icon="{ icon: ['fal', 'plus'], size: 'lg' }" />
        <template #dropdown>
          <ButtonComponent
            v-for="column in columns.filter(({ optional }) => optional)"
            :key="column.id"
            tag="button"
            :label="column.label"
            :icon="!column.disabled ? ['fal', 'check'] : undefined"
            @click="column.disabled = !column.disabled"
          />
        </template>
      </DropdownComponent>
      <TaskList :columns="columns" :filter="filter" />
    </div>
    <Complaint />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import StorageMixin from '@/mixins/Storage'

import FilterMixin from '@/components/Filter'
import HeaderComponent from '@/components/Header'
import { DropdownComponent } from 'vue-elder-dropdown'
import Actions from '@/components/Actions'

import TaskList from '@/components/Task/List/Component'
import Complaint from '@/components/Task/Complaint/Component'
import Badge from '@/components/Badge'
import Bus from '@/bus'

export default {
  mixins: [
    FilterMixin({ default: { search: '', project: '', status: ['request', 'undone'] } }),
    StorageMixin({
      target: 'columns',
      key() {
        return `${this.$route.name}:v1`
      },
      storage: 'localStorage',
      deep: true,
      map(e) {
        return e.filter(x => this.columns.find(c => c.id === x.id))
      },
    }),
  ],
  watch: {
    defaultFilter: {
      handler() {
        this.filter = {}
      },
      immediate: true,
    },
  },
  data() {
    return {
      columns: [
        { id: 'title', label: this.$t('title'), disabled: false },
        { id: 'status', label: this.$t('status') },
        { id: 'dueAt', label: this.$t('deadline'), optional: true, disabled: false },
        {
          id: 'project',
          label: this.$tc('project', 1),
          path: 'reference.reference.project.name',
          optional: false,
          disabled: false,
          condition: this.$route.meta.context === 'tasks',
        },
        {
          id: 'reference',
          component: 'Reference',
          label: this.$tc('unit', 1),
          path: 'reference.reference',
          optional: false,
          disabled: false,
          condition: this.$route.meta.context !== 'unit',
        },
        {
          id: 'type',
          label: this.$tc('type'),
          optional: true,
          disabled: true,
        },

        {
          id: 'members',
          label: this.$tc('member', 2),
          optional: true,
          disabled: false,
          helper: 'complaint-members-helper',
        },
        {
          id: 'createdBy',
          component: 'Members',
          label: this.$t('createdBy'),
          optional: true,
          disabled: true,
          helper: 'complaint-created-by-helper',
        },
        { id: 'updatedAt', label: this.$t('lastUpdated'), optional: true, disabled: true },
        { id: 'createdAt', label: this.$t('createdAt'), optional: true, disabled: true },
      ].filter(item => !('condition' in item) || item.condition),
      filterItems: [
        'search',
        'tags',
        'select:members',
        'checklist:status',
        this.$route.meta.context === 'tasks' ? 'select:project' : '',
      ],
      statusThemes: {
        request: 'primary',
        undone: 'warning',
        done: 'success',
        rejected: 'error',
      },
    }
  },
  computed: {
    ...mapState({
      statuses(state) {
        return (state.enums.TaskStatus || []).map(s => ({ value: s, label: this.$t(`taskStatus${s.toTitleCase()}`) }))
      },
    }),
    ...mapState('ResidentialAccount', ['items']),

    hasReference() {
      return this.$path('$route.params.id') && this.$path('$route.meta.type') === 'complaint'
    },

    filterOptions() {
      const base = {
        search: { placeholder: [this.$t('title')] },
        tags: { items: '/api/task/tags' },
        status: {
          label: this.$t('status'),
          selectAll: true,
          items: this.statuses,
        },
        members: {
          items: '/api/resource/user',
          label: this.$tc('member', 2),
          multiple: true,
        },
      }

      if (this.$route.meta.context === 'tasks')
        return {
          ...base,
          project: {
            items: '/api/resource/project',
            label: this.$tc('project', 1),
            multiple: false,
          },
        }

      return base
    },
    breadcrumb() {
      return [{ title: this.$metaInfo.title }]
    },
    statusFilterOptions() {
      const getStatusLabel = s => this.$t(`taskStatus${s.toTitleCase()}`)
      const getStatusTheme = s => {
        switch (s) {
          case 'request':
            return 'primary'
          case 'undone':
            return 'warning'
          case 'done':
            return 'success'
          case 'rejected':
            return 'error'
          default:
            return 'default'
        }
      }

      let items = this.$path('filter.status') || []
      items = items instanceof Array ? items : [items]
      let enums = this.statuses

      return {
        label: this.$t('status'),
        selected:
          items.length === enums.length || !items.length
            ? `<span class="badge badge--type-outlined badge--theme-default">${this.$t('showAll')}</span>`
            : items
                .map(
                  s =>
                    `<span style='margin-inline: .2rem' class="badge badge--type-outlined badge--theme-${getStatusTheme(
                      s,
                    )}">
                      ${getStatusLabel(s)}
                    </span>`,
                )
                .join('') || this.$t('none'),
        items: enums,
        selectAll: true,
      }
    },
  },

  methods: {
    ...mapActions('ResidentialAccount', {
      fetch(dispatch) {
        return dispatch('fetch', [
          { filter: { reference: this.$path('$route.params.id') } },
          { query: 'ResidentialAccountSelect' },
        ])
      },
    }),
    trigger() {
      Bus.$emit(this.$path('$route.meta.type') + ':show')
    },
  },
  metaInfo() {
    return {
      title: this.$path('$route.meta.title') ? this.$tc(...this.$path('$route.meta.title')) : this.$tc('task', 2),
    }
  },

  mounted() {
    if (this.hasReference && this.$path('$route.meta.context') === 'unit') this.fetch()
  },
  components: {
    HeaderComponent,
    TaskList,
    DropdownComponent,
    Badge,
    Complaint,
    Actions,
  },
}
</script>

<style lang="scss">
.tasks-view {
  background-color: $background-grey;
  .card__content {
    .tasks-table {
      max-height: 100vh;
    }
  }

  .breadcrumb .breadcrumb__item:first-child:last-child {
    display: none;
  }

  [data-filter='project'] {
    .kvass-selector__input {
      display: flex;

      & > * + * {
        margin-top: 0;
        margin-left: -1.5rem;
      }

      .field-select-modal__item {
        &-subtitle,
        &-thumbnail {
          display: none;
        }
      }
    }
  }
  [data-filter='members'] {
    .kvass-selector__input {
      display: flex;
      flex-direction: column;

      .field-select-modal__item-thumbnail {
        width: 1.5rem;
      }

      .field-select-modal__item-subtitle {
        display: none;
      }

      .kvass-selector {
        padding: 0.7rem 1rem;
      }
    }
  }

  [data-filter='status'] {
    .elder-button__label,
    .elder-button__icon {
      padding-block: 0.67rem;
      padding-inline: 0.67rem;
    }

    .elder-button__icon {
      padding-left: 0;
      padding-right: 1rem;
    }
  }
}
</style>
